import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import { getErrorMessage } from "helpers";
import { getSelectedMonthForHookForm } from "models";
import { creditReviewDetailsService } from "services";
import { uiActions } from "store";
import {
	BankStatementsDto,
	BankStatementsFormData,
	ErrorResponse,
} from "types";
import { floatWithComma2Normalizer } from "validators";

type useGetBankStatementsQueryProps = {
	creditReviewId: string;
};

const useGetBankStatementsQuery = ({
	creditReviewId,
}: useGetBankStatementsQueryProps) => {
	const dispatch = useDispatch();

	const mapBankStatementsData = (
		data: BankStatementsDto[],
	): BankStatementsFormData[] =>
		data?.map(
			(item: BankStatementsDto): BankStatementsFormData => ({
				month: getSelectedMonthForHookForm(item?.month),
				beginning:
					item?.beginning !== null
						? floatWithComma2Normalizer(item?.beginning)
						: "",
				credits:
					item?.credits !== null
						? floatWithComma2Normalizer(item?.credits)
						: "",
				debits:
					item?.debits !== null
						? floatWithComma2Normalizer(item?.debits)
						: "",
				ending:
					item?.ending !== null
						? floatWithComma2Normalizer(item?.ending)
						: "",
				average:
					item?.average !== null
						? floatWithComma2Normalizer(item?.average)
						: "",
			}),
		);

	const getDefaultFormValues = (
		data: BankStatementsDto[],
	): BankStatementsFormData[] => {
		let n = data?.length || 0;
		const values = mapBankStatementsData(data);

		while (n < 6) {
			n++;
			values.push({
				month: undefined,
				beginning: "",
				credits: "",
				debits: "",
				ending: "",
				average: "",
			});
		}
		return values;
	};

	const queryResult = useQuery(
		["bankStatementsInfo", creditReviewId],
		() =>
			creditReviewDetailsService.getBankStatementsInfo({
				creditReviewId,
			}),
		{
			select: (data: BankStatementsDto[]) => {
				return getDefaultFormValues(data);
			},
			onError(error: ErrorResponse) {
				dispatch(
					uiActions.showErrorNotification({
						title: getErrorMessage(error),
					}),
				);
			},
			enabled: !!creditReviewId,
			cacheTime: 0,
		},
	);

	return queryResult;
};

export default useGetBankStatementsQuery;
